import { AUTH_URL } from "../constants";
import { AUTH_KEY } from "../context/authContext";
import { APIRequest, HTTPClient, RequestType } from "../services/http_client"
import LocalStorageService from "../services/localstorage.service";
import { PermissionsType } from "../types/permissions";
import { SignInType, User } from "../types/users";

const client = new HTTPClient()

export interface UserResponse {
    access_token?: string;
    expires_in?: number;
    token_type?: string;
    message?: string;
    permissions?: Array<PermissionsType>;
}

export interface UsersList {
    users?: {
        data: Array<User>;
    };
    total?: number;
    error?: string;
}


export interface UsersFilters {
    profile: number;
    active: number;
    page?: number;
    limit?: number;
}

export interface UserForm {
    name: string;
    profile: number;
    password?: string;
    active: number;
    email: string;
}

export interface UserFormResponse {
    success: boolean;
    error?: string;
}


class UsersModel {
    static async create(data: UserForm){
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.POST,
            `${AUTH_URL}/users`,
            JSON.stringify(data),
            auth.accessToken,
        );

        const response = await client.post(request);

        if (!response.success) {
            return {
                error: response.errorMessage,
            }
        }
  
        return response.result as unknown as UserFormResponse
    }

    static async update(userId: number, data: UserForm){
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.PUT,
            `${AUTH_URL}/users/${userId}`,
            JSON.stringify(data),
            auth.accessToken,
        );

        const response = await client.put(request);

        if (!response.success) {
            return {
                error: response.errorMessage,
            }
        }
  
        return response.result as unknown as UserFormResponse
    }

    static async find(userId: number) {
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.GET,
            `${AUTH_URL}/users/${userId}`,
            undefined,
            auth.accessToken
          )
    
          const response = await client.get(request)
    
          if (!response.success) {
              return {
                error: response.errorMessage,
              }
          }
    
          return response.result as unknown as UsersList
    }

    static async delete(userId: number) {
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.DELETE,
            `${AUTH_URL}/users/${userId}`,
            undefined,
            auth.accessToken
          )
    
          const response = await client.delete(request)
    
          if (!response.success) {
              return {
                error: response.errorMessage,
              }
          }
    
          return response.result as unknown as UsersList
    }
    
    static async getAll({profile, active, page, limit} : UsersFilters) {
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            error: "Unauthorized"
        }

        const request = new APIRequest(
            RequestType.GET,
            `${AUTH_URL}/users?profile=${profile}&active=${active}&page=${page}&limit=${limit}`,
            undefined,
            auth.accessToken
          )
    
          const response = await client.get(request)
    
          if (!response.success) {
              return {
                error: response.errorMessage,
              }
          }
    
          return response.result as unknown as UsersList
    }

    static async signIn(data: SignInType): Promise<UserResponse> {
        const request = new APIRequest(
            RequestType.POST,
            `${AUTH_URL}/signin`,
            JSON.stringify(data)
          )
    
          const response = await client.post(request)
    
          if (!response.success) {
              return {
                message: response.errorMessage,
              }
          }
    
          return response.result as unknown as UserResponse
    }

    static async refreshToken(): Promise<UserResponse> {
        const auth = LocalStorageService.get(AUTH_KEY)
        if(!auth) return {
            message: "Unauthorized"
        }
        const request = new APIRequest(
            RequestType.POST,
            `${AUTH_URL}/refresh`,
            undefined,
            auth.accessToken
          )
    
          const response = await client.post(request)
    
          if (!response.success) {
              return {
                message: response.errorMessage,
              }
          }
    
          return response.result as unknown as UserResponse
    }
}

export default UsersModel